import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { ConfluenceSearchClient } from './confluence-search-client';
import memo from 'memoize-one';
import deepEqual from 'deep-equal';
import { ConfluenceRecentsClient } from './confluence-recents-client';
import { CollaborationGraphClient, extractSiteUrl } from '../../common/clients';
import { v4 as uuid } from 'uuid';
const defaultCollaborationGraphConfig = {
  collaborationGraphUrl: 'gateway/api/collaboration',
  useCollaborationGraphForRecents: false
};
const getSearchClientWithConfig = memo((config, collabGraphClient) => new ConfluenceSearchClient(config, collabGraphClient), deepEqual);
const getRecentsClientWithConfig = memo((config, collabGraphClient) => new ConfluenceRecentsClient(config, collabGraphClient), deepEqual);
const getCollabGraphClientWithConfig = memo(config => new CollaborationGraphClient({
  collaborationGraphUrl: config.collaborationGraphUrl,
  cloudId: config.cloudId,
  isMultiSite: config.isMultiSite,
  collabGraphSessionId: config.collabGraphSessionId,
  sites: config.sites
}), deepEqual);
export const ConfluenceSearchClientsContext = /*#__PURE__*/React.createContext({});
const MISSING_PROVIDER_ERROR = new Error(`Could not find all required Clients, make sure ConfluenceClientsProvider is created further up the tree`);

//Will generate a session id to be sent to the CG endpoint as well as the frontend analytics
//This is created here as CG results are cached inside search dialog and is not invoked for subsequent use actions
const COLLABORATION_GRAPH_SESSION_ID = uuid();
export const withClients = Component => {
  return props => /*#__PURE__*/React.createElement(ConfluenceSearchClientsContext.Consumer, null, ({
    searchClient,
    recentClient,
    cloudId,
    siteUrl = '',
    sites,
    collabGraphSessionId
  }) => {
    if (!searchClient || !recentClient) {
      throw MISSING_PROVIDER_ERROR;
    }
    const {
      ...rest
    } = props;
    return /*#__PURE__*/React.createElement(Component, _extends({}, rest, {
      searchClient: searchClient,
      recentClient: recentClient,
      cloudId: cloudId,
      collabGraphSessionId: collabGraphSessionId,
      siteUrl: siteUrl,
      sites: sites
    }));
  });
};
export const ConfluenceClientsProvider = ({
  children,
  config
}) => {
  const {
    baseUrl,
    cloudId,
    collaborationGraphUrl,
    aggregatorUrl,
    isUserAnonymous,
    useCollaborationGraphForRecents,
    recentItemsSupplier,
    sites = [],
    collabGraphSessionId,
    scopes,
    useGraphQLClient,
    useRecentWork,
    recentWorkAPIUrl
  } = {
    ...defaultCollaborationGraphConfig,
    ...config
  };
  const siteUrl = extractSiteUrl(config);
  const collabGraphClient = getCollabGraphClientWithConfig({
    collaborationGraphUrl: collaborationGraphUrl,
    cloudId: cloudId,
    isMultiSite: sites.length > 1,
    sites,
    collabGraphSessionId: collabGraphSessionId
  });
  return /*#__PURE__*/React.createElement(ConfluenceSearchClientsContext.Provider, {
    value: {
      searchClient: getSearchClientWithConfig({
        isUserAnonymous: isUserAnonymous,
        url: aggregatorUrl,
        cloudId: cloudId,
        isCollaborationGraphEnabled: useCollaborationGraphForRecents,
        siteMasterList: sites,
        scopes: scopes,
        useGraphQLClient
      }, collabGraphClient),
      recentClient: getRecentsClientWithConfig({
        url: baseUrl,
        isCollaborationGraphEnabled: useCollaborationGraphForRecents,
        recentItemsSupplier,
        useRecentWork,
        recentWorkAPIUrl
      }, collabGraphClient),
      collabGraphClient: collabGraphClient,
      siteUrl,
      cloudId,
      sites,
      collabGraphSessionId: COLLABORATION_GRAPH_SESSION_ID
    }
  }, children);
};
export const useClients = () => {
  const context = React.useContext(ConfluenceSearchClientsContext);
  if (!context.searchClient || !context.recentClient) {
    throw MISSING_PROVIDER_ERROR;
  }
  return context;
};
export const useRecentClient = () => {
  const context = React.useContext(ConfluenceSearchClientsContext);
  return context.recentClient;
};